import { useMemo } from 'react';
import { cn } from '@simplywallst/ui-core/next';
import PositiveNegativeValue from '@/components/PositiveNegativeValue';
import { PeriodSelect } from '../PeriodSelect';
import { BenchmarkOptionSelect } from '../BenchmarkOptionSelect';
import { useChartData } from '../../hooks/useChartData';

import {
  alignTimeseriesEndpoints,
  getBenchmarkMetricLabel,
  normaliseData,
  normaliseDataWithPercentageDiff,
} from '../../utils';

import type { getTrend } from '../../utils';
import type { Period } from '../../types';
import type { useAggregatePortfolioPerformanceTimeseries } from '../../hooks';
import type { BenchmarkType, useBenchmarkTimeseries } from '../../hooks';

import { formatterPercentage } from '@/utilities/format';
import { Chart } from '../Chart';

type Props = {
  benchmarkType: BenchmarkType;
  benchmarkOption: string;
  setBenchmarkOption: (option: string, type: BenchmarkType) => void;
  period: Period;
  setPeriod: (period: Period) => void;
  portfolioTimeseries: ReturnType<
    typeof useAggregatePortfolioPerformanceTimeseries
  >['data'];
  benchmarkTimeseries: ReturnType<typeof useBenchmarkTimeseries>['data'];
  startTimestamp: number;
  endTimestamp: number;
};

type Trend = ReturnType<typeof getTrend>;

const getValue = (trend: Trend): number | null => {
  if (trend === 'positive') return 1;
  if (trend === 'negative') return -1;
  return null;
};

export const PopulatedState = ({
  portfolioTimeseries,
  benchmarkTimeseries,
  benchmarkType,
  benchmarkOption,
  setBenchmarkOption,
  period,
  setPeriod,
  startTimestamp,
  endTimestamp,
}: Props) => {
  const {
    portfolio: normalisedPortfolioTimeseries,
    benchmark: normalisedBenchmarkTimeseries,
  } = useMemo(() => {
    return alignTimeseriesEndpoints(
      normaliseData(portfolioTimeseries),
      normaliseDataWithPercentageDiff(benchmarkTimeseries)
    );
  }, [portfolioTimeseries, benchmarkTimeseries]);

  const {
    xDomain,
    yDomain,
    interactionPoints,
    interactionIndex,
    setInteractionIndex,
    benchmarkMetric,
    portfolioMetric,
    portfolioTrend,
  } = useChartData({
    portfolioTimeseries: normalisedPortfolioTimeseries,
    benchmarkTimeseries: normalisedBenchmarkTimeseries,
  });

  return (
    <div className="relative grid gap-4 p-x2 lg:px-x3 lg:py-x2">
      <div className="grid grid-cols-2">
        <div className="grid grid-cols-[max-content_max-content] gap-9">
          <div className="grid gap-2">
            <div className="mt-x0_75 grid grid-cols-[min-content_auto] place-items-center gap-2">
              <div
                className={cn(
                  'h-x0_25',
                  'w-2',
                  portfolioTrend === 'positive'
                    ? 'bg-good'
                    : portfolioTrend === 'negative'
                      ? 'bg-bad'
                      : 'bg-p-white-100'
                )}
              />
              <p className="mb-0 text-xs text-soft">Holdings</p>
            </div>
            <p className="mb-0 text-sm font-strong">
              <PositiveNegativeValue value={getValue(portfolioTrend)}>
                {formatterPercentage(portfolioMetric)}
              </PositiveNegativeValue>
            </p>
          </div>
          <div className="grid gap-0.5">
            <div className="grid grid-cols-[min-content_auto] place-items-center gap-1">
              <div className="h-x0_25 w-2 bg-p-white-100/70" />
              <BenchmarkOptionSelect
                option={benchmarkOption}
                setOption={setBenchmarkOption}
                label={getBenchmarkMetricLabel(benchmarkType, benchmarkOption)}
              />
            </div>

            <p className="mb-0 text-sm font-strong text-solid">
              {formatterPercentage(benchmarkMetric)}
            </p>
          </div>
        </div>
        <PeriodSelect period={period} setPeriod={setPeriod} />
      </div>
      <Chart
        period={period}
        portfolioTimeseries={normalisedPortfolioTimeseries}
        benchmarkTimeseries={normalisedBenchmarkTimeseries}
        interactionPoints={interactionPoints}
        interactionIndex={interactionIndex}
        setInteractionIndex={setInteractionIndex}
        xDomain={xDomain}
        yDomain={yDomain}
        startTimestamp={startTimestamp}
        endTimestamp={endTimestamp}
      />
    </div>
  );
};
